import { DateTime } from 'luxon';

import { MetricsGroupBy as InquiryMetricsGroupBy } from 'dashboard/models/metrics';
import { MetricsGroupBy as VerificationMetricsGroupBy } from 'dashboard/models/metrics-verification';
import { ApiModel, ApiResource } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

export enum AnalyticsQueryTemplateStatus {
  Active = 'active',
  Archived = 'archived',
}

export enum QueryType {
  Inquiries = 'inquiries',
  VerificationsVolume = 'verifications-volume',
  VerificationsChecks = 'verifications-checks',
}

export interface RelativeTimeSpan {
  interval: number;
  unit: 'days' | 'months';
}

export interface InquiryAnalyticsQueryParams {
  type: QueryType;
  'template-ids': string[];
  'inquiry-template-ids': string[];
  'country-codes': string[];
  'device-groups': string[];
  'group-by': InquiryMetricsGroupBy | null;
  'tag-ids': string[];
  platforms: string[];
}

export interface VerificationVolumeAnalyticsQueryParams {
  type: QueryType;
  'template-ids': string[];
  'inquiry-template-ids': string[];
  'verification-types': string[];
  'group-by': VerificationMetricsGroupBy | null;
}

export interface VerificationChecksAnalyticsQueryParams
  extends VerificationVolumeAnalyticsQueryParams {
  'check-requirements': string[];
  'check-types': string[];
}

export type QueryParams =
  | InquiryAnalyticsQueryParams
  | VerificationVolumeAnalyticsQueryParams
  | VerificationChecksAnalyticsQueryParams;

export interface AnalyticsQueryTemplateAttributes {
  name: string;
  status: AnalyticsQueryTemplateStatus;
  'updated-at': string;
  'last-updater': string;
  'relative-time-span': RelativeTimeSpan;
  query: QueryParams;
}

interface AnalyticsQueryTemplateRelationships {}

export type AnalyticsQueryTemplate = ApiResource<
  AnalyticsQueryTemplateAttributes,
  AnalyticsQueryTemplateRelationships,
  ApiModel.AnalyticsQueryTemplate
>;

/**
 * Calculate offset for loading query templates based on relative time span.
 * @param {RelativeTimeSpan} relativeTimeSpan - The relative time span to compute the offset for
 * @param {moment.Moment} defaultEndAt - The default end time for analytics date filter (e.g. today)
 * @returns {Object} An object containing the start and end moments for the query template load
 */
export const computeOffsetForQueryTemplateLoad = (
  relativeTimeSpan: RelativeTimeSpan,
  defaultEndAt: DateTime,
): {
  createdAtStart: DateTime;
  createdAtEnd: DateTime;
} => {
  let startAt: DateTime;

  const { unit, interval } = relativeTimeSpan;

  switch (unit) {
    //  Note: The interval for 'months' works in a specific way:
    //  * { interval: 1: unit: 'months' } represents the current month, 'This month'.
    //  * { interval: 2: unit: 'months' } represents the previous month, 'Last month'.
    //  Although this may seem less intuitive and might be problematic in terms of scalability and understandability,
    //  given a short time frame for implementation it fits the current design requirements with little prospect of changing for now.
    case 'months': {
      const offset = interval === 2 ? 1 : 0;
      const referencedMonth = defaultEndAt.minus({ months: offset });

      // Calculate start time
      startAt = referencedMonth.set({ day: 1 }).startOf('day');

      // If there was an offset, adjust the end time to be the end of the referenced month
      if (offset > 0) {
        defaultEndAt = referencedMonth.endOf('month').endOf('day');
      }
      break;
    }
    // Compute offset for 'days' unit
    case 'days': {
      startAt = defaultEndAt.minus({ days: interval }).startOf('day');
      break;
    }
    default:
      assertNever(unit);
  }

  // Return the start and end times
  return {
    createdAtStart: startAt,
    createdAtEnd: defaultEndAt,
  };
};

export const QUERY_TYPE_TO_SHORTNAME_MAP: Record<QueryType, string> = {
  [QueryType.Inquiries]: 'Inquiry',
  [QueryType.VerificationsVolume]: 'Volume',
  [QueryType.VerificationsChecks]: 'Checks',
};

export const QUERY_TYPE_TO_PATH_MAP: Record<QueryType, string> = {
  [QueryType.VerificationsVolume]: '/dashboard/verification-analytics/volume',
  [QueryType.VerificationsChecks]: '/dashboard/verification-analytics/checks',
  [QueryType.Inquiries]: '/dashboard/inquiry-analytics/volume',
};
